import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Grid, Container, Breadcrumbs, Link, IconButton, CircularProgress, useTheme, Divider, Chip, Tooltip, Avatar } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import MainDrawer, { openDrawerWidth, collapsedDrawerWidth } from "../MainDrawer";
import queryGraphql from "../../helpers/ApiInterface";
import MiniBot from '../MiniBot';
import { useAuth } from '../../helpers/AuthProvider';
import type { SxProps, Theme } from '@mui/material/styles'; 
// --- Interfaces ---
interface CostLabelPair {
    cost: string;
    label: string;
}

interface APIData {
    id: string;
    name: string;
    cloudProvider: string;
    environment: string;
    status: string;
    reasonForRejection?: string;
    costLabelPairs: CostLabelPair[];
    apiBackendOwner: { id: string; name: string; }; // Renamed field
    application: { id: string; applicationName: string; };
    service: { id: string; serviceName: string; };
    // Flat properties from the new API
    codeTemplate: string;
    path: string;
    httpMethod: string;
    apiVersion: string;
    description: string;
    mockRequest: string;
    mockResponse: string;
    serviceType: string;
}

// --- Helper Component ---
const DetailItem: React.FC<{ label: string; value?: string | null ; sx?: SxProps<Theme>;}> = ({ label, value }) => {
    const displayValue = value || '-';
    return (
        <Box mb={2}>
            <Typography fontFamily="Gilroy-Regular" fontSize="12px" color="text.secondary">{label}</Typography>
            <Typography fontFamily="Gilroy-Semibold" fontSize="14px" sx={{ wordBreak: 'break-all' }}>{displayValue}</Typography>
        </Box>
    );
};

const CodeBlockItem: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => {
    const displayValue = value || 'Not provided';
    return (
        <Box >
             <Typography fontFamily="Gilroy-Regular" fontSize="12px" color="text.secondary" mb={1}>{label}</Typography>
             <Box component="pre" sx={{
                background: '#f5f5f5',
                padding: 3,
                borderRadius: '4px',
                height:'300px',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
                fontFamily: 'monospace',
                fontSize: '13px',
                flexGrow: 1,       // Keep internal styles
                overflowY: 'auto',  // Keep internal styles
                m: 0
             }}>
                {displayValue}
            </Box>
        </Box>
    );
};

// --- Main Content Component ---
const APIDetailsPageContent: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [api, setApi] = useState<APIData | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const { user } = useAuth();
    const [apiEndpoint, setApiEndpoint] = useState<string | null>(null);
    const [apiRepo, setApiRepo]= useState<string | null>(null);
    useEffect(() => {
        const fetchAPIData = async () => {
            if (!id) {
                setError("API ID is missing from URL.");
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            try {
                const query = `
                    query GetAPIBackend($getApiBackendId: String!) {
                      getAPIBackend(id: $getApiBackendId) {
                        id
                        name
                        cloudProvider
                        environment
                        status
                        apiBackendOwner { id, name }
                        application { id, applicationName }
                        service { id, serviceName }
                        reasonForRejection
                        costLabelPairs { cost, label }
                        codeTemplate
                        path
                        httpMethod
                        apiVersion
                        description
                        mockRequest
                        mockResponse
                        serviceType
                      }
                    }
                `;
                // Use the new variable name
                const response = await queryGraphql(query, { getApiBackendId: id });
                
                // Use the new response key
                if (response.getAPIBackend) {
                    setApi(response.getAPIBackend); // Set the flat response
                } else {
                    setError("API not found or an error occurred.");
                }
            } catch (err: any) {
                setError(err.message || "Failed to fetch API details.");
            } finally {
                setIsLoading(false);
            }
        };

        fetchAPIData();
    }, [id]);
    useEffect(() => {
                    const fetchEndpointUrl = async () => {
                        // Only run if we have an active API with an ID
                        if (!api || api.status !== 'active' || !api.id) return;

                        try {
                            const data = await queryGraphql(
                                `query Query($resourceStatusId: String!) {
                                resourceStatus(id: $resourceStatusId) {
                                    solutionEndpointUrl
                                    projectUrl
                                }
                                }`, 
                                { resourceStatusId: api.id }
                            );
                            if (data.resourceStatus?.solutionEndpointUrl) {
                                setApiEndpoint(data.resourceStatus.solutionEndpointUrl);
                                setApiRepo(data.resourceStatus.projectUrl)
                            }
                        } catch (error) {
                            console.log("Error fetching API endpoint URL:", error);
                        }
                    };

                    fetchEndpointUrl();
                }, [api]); // This effect depends on the 'api' object

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress size={24} />
                <Typography sx={{ ml: 2 }}>Loading API Backend details...</Typography>
            </Box>
        );
    }

    if (error) {
        return <Typography color="error" sx={{ p: 2 }}>{error}</Typography>;
    }

    if (!api) {
        return <Typography sx={{ p: 2 }}>No API Backend data available.</Typography>;
    }

    const getStatusBackgroundColor = (status: string): string => {
        const lowerStatus = status.toLowerCase();
        if (lowerStatus === "active" || lowerStatus === "approved") return "#12B76A10";
        if (lowerStatus === "rejected") return "#F0443810";
        if (lowerStatus === "pending") return "#FDB02210";
        return "#F8F8F8";
    };

    return (
        <Container maxWidth={false} sx={{ mt: 2, mb: 2 }}>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2, fontSize: "14px", color: "gray" }}>
                <Link underline="hover" color="inherit" href="/apis" onClick={(e) => { e.preventDefault(); navigate('/apis'); }}>APIs</Link>
                <Typography color="text.primary" sx={{ fontSize: "14px" }}>API Backend Details</Typography>
            </Breadcrumbs>

            <Box display="flex" alignItems="center" mb={3}>
                <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate('/apis')}>
                    <ArrowBackIosNewIcon fontSize="small" />
                </IconButton>
                <Typography variant="h5" component="h1" sx={{ fontFamily:'Gilroy-Bold', fontSize: "18px" }}>API Backend Details</Typography>
            </Box>

            <Box sx={{ boxShadow: "0px 3px 6px #10101029", borderRadius: "8px" }}>
                <Box sx={{ p: 2.5, bgcolor: getStatusBackgroundColor(api.status), borderTopLeftRadius: "8px", borderTopRightRadius: "8px" }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" component="h2" sx={{ fontFamily: 'Gilroy-Bold', fontSize: '20px' }}>
                            {api.name}
                        </Typography>
                        {user.roles?.some((e: string) => ["developer"].includes(e)) && (api.status =="pending" || api.status =="active" ) && (
                            <IconButton sx={{ p: 0.5 }} onClick={() => navigate(`/apis/edit/${id}`)}>
                                <img width="20px" height="20px" src="/assets/edit-icon.svg" alt="Edit"/>
                            </IconButton>
                        )}
                    </Box>
                    <Typography variant="body2" sx={{ fontFamily:'Gilroy-Regular', fontSize:'14px', color:'#666666' }}>
                        Resource Owner: {api.apiBackendOwner.name}
                    </Typography>
                </Box>

                <Grid container spacing={3} sx={{ p: 3, pt: 2.5 }}>
                    <Grid item xs={12} sm={4} md={3}><DetailItem label="Application Name" value={api.application?.applicationName} /></Grid>
                    <Grid item xs={12} sm={4} md={3}><DetailItem label="Service Name" value={api.service?.serviceName} /></Grid>
                    <Grid item xs={12} sm={4} md={3}><DetailItem label="Cloud Provider" value={api.cloudProvider?.toUpperCase()} /></Grid>
                    {/* <Grid item xs={12} sm={4} md={3}><DetailItem label="Environment" value={api.environment} /></Grid> */}
                </Grid>

                <Box sx={{ mt: 0, p: 2.5, borderTop: "1px solid #e0e0e0" }}>
                    <Typography component="h3" sx={{ fontFamily: 'Gilroy-Medium', fontSize: '14px', pb: 1 }}>API Backend Specification</Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={6} sm={3}><DetailItem label="Path" value={api.path} /></Grid>
                        <Grid item xs={6} sm={3}><DetailItem label="HTTP Method" value={api.httpMethod} /></Grid>
                        <Grid item xs={6} sm={3}><DetailItem label="API Version" value={api.apiVersion} /></Grid>
                        <Grid item xs={6} sm={3}><DetailItem label="Code Template" value={api.codeTemplate} /></Grid>
                        {/* {apiEndpoint && (
                                <Grid item xs={6} sm={3} >
                                    <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>
                                        API Backend Endpoint
                                    </Typography>
                                    <Tooltip
                                        title={
                                            <Link
                                                href={apiEndpoint}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{ color: '#ffffff', textDecoration: 'underline' }}
                                            >
                                                Endpoint
                                            </Link>
                                        }
                                    >
                                        <IconButton sx={{p: 0}}>
                                            <Avatar
                                                src="/assets/info.svg"
                                                alt="info"
                                                sx={{ height: 16, width: 16, cursor: 'pointer' }}
                                            />
                                        </IconButton>
                                    </Tooltip>
                                </Grid>
                            )} */}

                            {apiRepo && (
    <Grid item xs={6} sm={3}>
        <Typography 
            variant="caption" 
            display="block" 
            sx={{ fontFamily: 'Gilroy-Regular', fontSize: '12px', color: '#666666' }}
        >
            API Backend Repo
        </Typography>
        <Tooltip
            title={
                <>
                    <Typography 
                        fontFamily="Gilroy-Bold" 
                        fontSize="14px"
                    >
                        API Backend Repo
                    </Typography>
                    
                    {/* Conditionally render Link vs. plain text */}
                    {apiRepo && apiRepo !== "Loading..." ? (
                        <Link
                            href={`${apiRepo}/-/tree/feature1_dev/apis/${api.name}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                color: '#ffffff',
                                textDecoration: 'underline',
                                wordBreak: 'break-all' // Ensures long URLs wrap
                            }}
                        >
                            Repository URL
                        </Link>
                    ) : (
                        <Typography 
                            fontFamily="Gilroy-Regular" 
                            fontSize="12px" 
                            sx={{ color: '#ffffff' }}
                        >
                            Loading...
                        </Typography>
                    )}
                </>
            }
            slotProps={{ tooltip: { sx: { bgcolor: '#333333' } } }}
        >
            <IconButton sx={{ p: 0 }}>
                <Avatar
                    src="/assets/info.svg"
                    alt="info"
                    sx={{ height: 16, width: 16, cursor: 'pointer' }}
                />
            </IconButton>
        </Tooltip>
    </Grid>
)}

                    </Grid>
                </Box>
            </Box>

            <Box mt={2} bgcolor="#fff" p={3} borderRadius="12px" boxShadow={1}>
                <Typography fontSize="14px" fontFamily="Gilroy-SemiBold">Description & Payloads</Typography>
                <Divider sx={{ marginY: 2 }} />
                <Grid container spacing={3} >
                    <Grid item xs={12}><DetailItem label="Description" value={api.description} /></Grid>
<Grid item xs={12} md={6} sx={{ minHeight: '400px', overflowY: 'auto' }}><CodeBlockItem label="Mock Request" value={api.mockRequest} /></Grid>
<Grid item xs={12} md={6} sx={{ minHeight: '400px', overflowY: 'auto' }}><CodeBlockItem label="Mock Response" value={api.mockResponse} /></Grid>
                </Grid>
            </Box>
            
            <Box mt={2} bgcolor="#fff" p={3} borderRadius="12px" boxShadow={1}>
                <Typography fontSize="14px" fontFamily="Gilroy-SemiBold">Tags / Labels</Typography>
                <Divider sx={{ marginY: 2 }} />
                <Box>
                    {api.costLabelPairs.map(pair => (
                        <Chip key={pair.cost} label={`${pair.cost}: ${pair.label}`} sx={{ mr: 1, mb: 1 }} />
                    ))}
                </Box>
            </Box>
            
            {api.status === 'rejected' && (
                <Box mt={2} bgcolor="#fff" p={3} borderRadius="12px" boxShadow={1}>
                    <Typography fontSize="14px" fontFamily="Gilroy-SemiBold">Rejection Details</Typography>
                    <Divider sx={{ marginY: 2 }} />
                    <DetailItem label="Reason for Rejection" value={api.reasonForRejection} />
                </Box>
            )}
        </Container>
    );
};

// --- Main Page Component ---
const APIDetailsPage: React.FC = () => {
    const theme = useTheme();
    const getInitialDrawerState = () => {
        return typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('isFaradayDrawerOpen') || 'true') : true;
    };
    const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(getInitialDrawerState);
    const handleDrawerStateChange = (isOpen: boolean) => setIsDrawerActuallyOpen(isOpen);
    const currentMarginLeft = isDrawerActuallyOpen ? openDrawerWidth - 248 : collapsedDrawerWidth - 70;

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <MainDrawer index={"apis-backend"} onStateChange={handleDrawerStateChange} />
            <MiniBot />
            <Box component="main" sx={{
                flexGrow: 1,
                p: 0,
                transition: theme.transitions.create('margin', {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.enteringScreen,
                }),
                marginLeft: { xs: 0, sm: `${currentMarginLeft}px` },
            }}>
                <APIDetailsPageContent />
            </Box>
        </Box>
    );
};

export default APIDetailsPage;